// Resolution-variant sibling folders in the press-kit tree.
//
// A photographer's folder normally holds the same pictures several times over —
// `jpeg-lowres`, `jpeg-midres`, `jpeg-highres`, `tiff` — so showing every
// sibling loads and renders each photo three or four times. The folder browser
// shows one of them, chosen by the "Image versions" setting on the press-kit
// header; the rest are left in Dropbox and never fetched.
//
// Folder names are matched on their letters and digits alone, so `jpeg-highres`,
// `JPEG_HighRes` and `jpeghighres` all resolve to the same variant.

// Listed best-first: "Best available" walks this order and takes the first
// variant a folder actually has, so the full-size JPEG wins over the web export
// and the raw/TIFF masters come last.
const VARIANT_ALIASES = [
  { key: 'high',  label: 'High-res JPEG', names: ['jpeghighres', 'jpghighres', 'jpeghires', 'jpghires', 'highres', 'hires', 'highresolution', 'fullres', 'fullresolution'] },
  { key: 'print', label: 'Print',         names: ['jpegprint', 'jpgprint', 'printres', 'print'] },
  { key: 'mid',   label: 'Mid-res JPEG',  names: ['jpegmidres', 'jpgmidres', 'jpegmediumres', 'midres', 'medres', 'mediumres'] },
  { key: 'web',   label: 'Web JPEG',      names: ['jpegweb', 'jpgweb', 'webres', 'webready', 'web'] },
  { key: 'low',   label: 'Low-res JPEG',  names: ['jpeglowres', 'jpglowres', 'lowres', 'lores'] },
  { key: 'jpeg',  label: 'JPEG',          names: ['jpeg', 'jpg', 'jpegs', 'jpgs'] },
  { key: 'png',   label: 'PNG',           names: ['png', 'pngs'] },
  { key: 'tiff',  label: 'TIFF',          names: ['tiff', 'tif', 'tiffs', 'tifs'] },
  { key: 'raw',   label: 'RAW',           names: ['raw', 'raws', 'rawfiles'] },
];

export const VERSION_PREF_AUTO = 'auto';
export const VERSION_PREF_ALL = 'all';

// The "Image versions" setting's options, in menu order.
export const VERSION_OPTIONS = [
  { value: VERSION_PREF_AUTO, label: 'Best available' },
  ...VARIANT_ALIASES.map(v => ({ value: v.key, label: v.label })),
  { value: VERSION_PREF_ALL, label: 'All versions' },
];

// A lone variant folder is just a folder — there is nothing to choose between
// until a sibling holds the same images at another size.
export const MIN_VARIANT_GROUP = 2;

const VERSION_PREF_STORAGE_KEY = 'comms_asset_version_pref';

export function readVersionPref() {
  try {
    const stored = localStorage.getItem(VERSION_PREF_STORAGE_KEY);
    if (stored && VERSION_OPTIONS.some(o => o.value === stored)) return stored;
  } catch { /* private browsing — fall back to the default */ }
  return VERSION_PREF_AUTO;
}

export function writeVersionPref(pref) {
  try { localStorage.setItem(VERSION_PREF_STORAGE_KEY, pref); } catch { /* nothing to do */ }
}

const norm = name => (name || '').toLowerCase().replace(/[^a-z0-9]+/g, '');

function variantIndex(folderName) {
  const n = norm(folderName);
  if (!n) return -1;
  return VARIANT_ALIASES.findIndex(v => v.names.includes(n));
}

// The variant group a folder name belongs to ('high', 'tiff', …), or null when
// the name is an ordinary folder.
export function variantKey(folderName) {
  const i = variantIndex(folderName);
  return i === -1 ? null : VARIANT_ALIASES[i].key;
}

// Rank within the best-first order above; null for ordinary folders.
export function variantRank(folderName) {
  const i = variantIndex(folderName);
  return i === -1 ? null : i;
}

// Splits a folder listing into the variant group and everything else.
// `variantItems` come back best-first. With fewer than MIN_VARIANT_GROUP
// variants nothing is grouped and every folder stays in `plainItems`.
export function splitVariantFolders(items, nameOf = x => x) {
  const ranked = (items || []).map((item, i) => ({ item, i, rank: variantRank(nameOf(item)) }));
  const variants = ranked.filter(r => r.rank != null);
  if (variants.length < MIN_VARIANT_GROUP) {
    return { variantItems: [], plainItems: items || [], preferredName: null };
  }
  variants.sort((a, b) => a.rank - b.rank || a.i - b.i);
  return {
    variantItems: variants.map(v => v.item),
    plainItems: ranked.filter(r => r.rank == null).map(r => r.item),
    preferredName: nameOf(variants[0].item),
  };
}

// The one variant folder to show out of a listing, for the given preference:
// the requested version when the folder has it, the best available otherwise.
// null means "nothing to choose" — no variant group here, or the preference is
// to show them all.
export function chosenVariant(folderNames, pref = VERSION_PREF_AUTO) {
  if (pref === VERSION_PREF_ALL) return null;
  const { variantItems, preferredName } = splitVariantFolders(folderNames || []);
  if (!variantItems.length) return null;
  if (pref && pref !== VERSION_PREF_AUTO) {
    const named = variantItems.find(name => variantKey(name) === pref);
    if (named) return named;
  }
  return preferredName;
}

// Drops the variant folders the preference is not showing, keeping ordinary
// folders and their order untouched.
export function filterVariantFolders(items, nameOf = x => x, pref = VERSION_PREF_AUTO) {
  const chosen = chosenVariant((items || []).map(nameOf), pref);
  if (!chosen) return items || [];
  return (items || []).filter(item => variantRank(nameOf(item)) == null || nameOf(item) === chosen);
}

// Folder names the expand-all cascade must leave closed: the variants that
// aren't being shown. Opening all of them is what made the page crawl on
// projects with a full set of exports.
export function skippedVariantNames(folderNames, pref = VERSION_PREF_AUTO) {
  const chosen = chosenVariant(folderNames, pref);
  if (!chosen) return new Set();
  return new Set((folderNames || []).filter(name => variantRank(name) != null && name !== chosen));
}
